import { InputLabel, MenuItem } from '@mui/material';
import { memo, useMemo } from 'react';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValoreDominioVO } from 'shell/models/act-wizard';
import { HttpResponse } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { ItemTemplateVM } from './list-template-transcription';
import { IdAnnotations } from 'civil-status/constants/id-annotations';

interface ListTemplateAnnotationsProps {
  disabled?: boolean;
  required?: boolean;
  value: string;
  onChange: (value?: ItemTemplateVM) => void;
  error?: boolean;
  isSmallSize?: boolean;
  idDominio: string;
  tipoEvento?: string;
  label: string;
  withPropertyRegime?: boolean;
}

const ListTemplateAnnotationsComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  isSmallSize = false,
  idDominio,
  label,
  tipoEvento,
  withPropertyRegime = false,
}: ListTemplateAnnotationsProps): React.ReactElement => {
  const [conditions] = useHttpFetch<HttpResponse<Array<ValoreDominioVO>>, Array<ItemVM>>(
    `${__ACT_API_URL__}/valori-dominio-atti/id-dominio/${idDominio}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) => {
        let castedResponse = (response.data.payload || []) as Array<ValoreDominioVO>;

        if (tipoEvento) {
          castedResponse = castedResponse.filter((item) => item.idValoreAnsc === tipoEvento);
        }

        if (withPropertyRegime) {
          castedResponse = castedResponse.filter(
            (item) =>
              item.key?.idValore !== IdAnnotations.AltraLegge &&
              item.key?.idValore !== IdAnnotations.ConvenzioniMatrimoniali &&
              item.key?.idValore !== IdAnnotations.SeparazioneDeiBeni
          );
        }

        return castedResponse.map((item) => ({
          id: item.key?.idValore,
          value: item.descrizioneBreveValore,
          valueTemplate: item.valore,
        })) as Array<ItemVM>;
      },
    }
  );

  const conditionsData: Array<ItemVM> = useMemo(() => {
    return (conditions.data || []) as Array<ItemVM>;
  }, [conditions.data]);

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={`${label}-label`}>
        {required ? `${label} *` : label}
      </InputLabel>
      <PmiSelect
        labelId={`${label}-label`}
        id={label}
        name={label}
        value={value}
        label={required ? `${label} *` : label}
        onChange={(event) =>
          onChange(conditionsData?.find((item) => item.id === event.target.value))
        }
        disabled={disabled}
        error={error}
        size={isSmallSize ? 'small' : undefined}
      >
        {conditionsData?.map((condition: ItemVM) => (
          <MenuItem key={condition.id} value={condition.id}>
            {condition.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListTemplateAnnotationsComponent.displayName = 'List Conditions component';

const ListTemplateAnnotations = memo(ListTemplateAnnotationsComponent);

export default ListTemplateAnnotations as typeof ListTemplateAnnotationsComponent;
